/**
 * CrudResource Feature - основна точка входу
 * 
 * Об'єднує всі модулі feature в єдиний об'єкт для експорту
 * Тепер підтримує гнучку кастомізацію через handlersFactory
 * 
 * Структура:
 * - headerPage.eventHandlers - обробники для HeaderPage компонента
 * - eventHandlers - основні обробники (onAfterInit, onButtons)
 * - table.eventHandlers - обробники для Table компонента
 */

import { createCrudResourceHandlers, type ICrudResourceHandlersConfig } from './handlersFactory'
import { AddTableViewModeToList } from './tableModeFeature'
import { InitTableSettings } from './tableSettingsFeature'

// Експорт констант та утиліт для використання в інших частинах додатку
export { AddTableModeCrudFeature } from './constants'
export { transformFieldValue } from './utils'
export { createMenuItemsFunction } from './menuItemsFactory'
export * from './types'

// Експорт handlers для можливості кастомізації
export * from './handlers'

// Експорт factory та типів для кастомізації
export { createCrudResourceHandlers, type ICrudResourceHandlersConfig }

/**
 * Створює CrudResourceFeature з можливістю кастомізації handlers
 * 
 * @param config - конфігурація для переопределения handlers
 * @returns об'єкт CrudResourceFeature
 */
export const createCrudResourceFeature = (config: ICrudResourceHandlersConfig = {}) => {
    const handlers = createCrudResourceHandlers(config)

    return {
        /**
         * Event handlers для HeaderPage компонента
         */
        headerPage: {
            eventHandlers: {
                onButtons: handlers.headerPage.onButtons,
                onBeforeInit: handlers.headerPage.onBeforeInit,
                onAfterInit: handlers.headerPage.onAfterInit,
            },
        },

        /**
         * Основні event handlers для CRUD ресурсу
         */
        eventHandlers: {
            /**
             * Обробники що виконуються після ініціалізації CrudResourceContext
             */
            onAfterInit: {
                /**
                 * Додає табличний режим перегляду до списку режимів
                 */
                AddTableViewModeToList,
                /**
                 * Батчево підвантажує персональні налаштування колонок і фільтра
                 */
                InitTableSettings,
            },

            /**
             * Обробники для кнопок в формах ресурсів
             */
            onButtons: handlers.formButtons,
        },

        /**
         * Event handlers для Table компонента
         */
        table: {
            eventHandlers: {
                onBeforeInit: handlers.table.onBeforeInit,
                onAfterInit: {}, // Поки що порожній, може бути розширений
                onBuildColumns: handlers.table.onBuildColumns,
                onBuildDataSourceItem: handlers.table.onBuildDataSourceItem,
            },
        },
    }
}

/**
 * Основний об'єкт CrudResourceFeature зі стандартною конфігурацією
 * Використовується за замовчуванням, якщо не потрібна кастомізація
 */
export const CrudResourceFeature = createCrudResourceFeature()
